import { Fragment, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { COUNTRY_NAMES } from '@/constants/countries';
import { cn } from '@/utils/cn';

export interface CountryComboboxProps {
  /** Selected country name. */
  value: string;
  /** Called with the selected country name. */
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  ariaLabel?: string;
}

const inputClasses =
  'h-10 w-full rounded-lg border border-secondary-300 bg-white py-2 pl-3 pr-9 text-sm ring-offset-white placeholder:text-secondary-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-800 dark:text-white dark:ring-offset-secondary-900 dark:placeholder:text-secondary-400';

export function CountryCombobox({
  value,
  onChange,
  label,
  required,
  placeholder = 'Country',
  error,
  disabled,
  ariaLabel,
}: CountryComboboxProps) {
  const [query, setQuery] = useState('');

  const filtered =
    query.trim() === ''
      ? COUNTRY_NAMES
      : COUNTRY_NAMES.filter((name) => name.toLowerCase().includes(query.trim().toLowerCase()));

  return (
    <div className="w-full">
      {label && (
        <label className="mb-2 block text-sm font-medium text-secondary-900 dark:text-secondary-100">
          {label}
          {required && <span className="ml-1 text-danger-500">*</span>}
        </label>
      )}
      <Combobox value={value} onChange={(v) => onChange(v ?? '')} disabled={disabled}>
        <div className="relative">
          <div className="relative">
            <Combobox.Input
              aria-label={ariaLabel ?? label ?? placeholder}
              className={cn(inputClasses, error && 'border-danger-300 focus-visible:ring-danger-500 dark:border-danger-600')}
              placeholder={placeholder}
              displayValue={(name: string) => name}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
            </Combobox.Button>
          </div>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-75"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-secondary-200 bg-white py-1 text-sm shadow-xl focus:outline-none dark:border-secondary-700 dark:bg-secondary-900">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-secondary-500 dark:text-secondary-400">No countries found.</div>
              ) : (
                filtered.map((name) => (
                  <Combobox.Option
                    key={name}
                    value={name}
                    className={({ active }) =>
                      cn(
                        'relative cursor-pointer select-none py-2 pl-3 pr-9',
                        active
                          ? 'bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100'
                          : 'text-secondary-900 dark:text-secondary-100',
                      )
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={cn('block truncate', selected && 'font-semibold')}>{name}</span>
                        {selected && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 text-primary-600 dark:text-primary-400">
                            <CheckIcon className="h-4 w-4" />
                          </span>
                        )}
                      </>
                    )}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
      {error && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
}
